/**
 * useErrorTracker — hook global qui capture les erreurs JS non-catchées
 * (window.onerror + unhandledrejection) et les POST vers /api/reports/error.
 *
 * 11/02/2026 (Axe 1c fiabilité) — Objectif : ne plus dépendre uniquement
 * des remontées manuelles user + captures d'écran. Chaque erreur silencieuse
 * qui apparaît côté client (Safari private mode, network fail, undefined
 * ref, …) est loggée en clair côté backend avec la clé licence si connue.
 *
 * Design :
 *   - Une seule instance globale (montage dans App.jsx via ce hook)
 *   - Throttle : max 1 log toutes les 5 s par empreinte d'erreur (évite
 *     de spammer si une même exception fire en boucle)
 *   - Best-effort : n'attend jamais la réponse backend
 *   - License key lue à chaud depuis licenseStore (peut être null au tout début)
 */
import { useEffect } from 'react';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const THROTTLE_MS = 5000;
const _throttleMap = new Map(); // key: fingerprint → lastSentAt (ms)

/**
 * Empreinte compacte pour throttling. Combine message + première ligne du
 * stack. Réduit le spam sans occulter les erreurs légitimement distinctes.
 */
function fingerprint(errorMessage, errorStack) {
  const firstStackLine = String(errorStack || '').split('\n')[0] || '';
  return `${errorMessage}::${firstStackLine}`.slice(0, 200);
}

function shouldThrottle(errMsg, errStack) {
  const fp = fingerprint(errMsg, errStack);
  const now = Date.now();
  const last = _throttleMap.get(fp) || 0;
  if (now - last < THROTTLE_MS) return true;
  _throttleMap.set(fp, now);
  // Prune old entries so the map doesn't grow indefinitely.
  if (_throttleMap.size > 200) {
    const cutoff = now - 5 * 60 * 1000;
    for (const [k, ts] of _throttleMap.entries()) {
      if (ts < cutoff) _throttleMap.delete(k);
    }
  }
  return false;
}

async function reportError(errMsg, errStack) {
  if (!BACKEND) return;
  if (shouldThrottle(errMsg, errStack)) return;
  try {
    await fetch(`${BACKEND}/api/reports/error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        license_key: useLicenseStore.getState().licenseKey || '',
        error_message: String(errMsg || 'Unknown error').slice(0, 2000),
        error_stack: String(errStack || '').slice(0, 8000),
        page_url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      }),
    });
  } catch (_) {
    // fail silently — logging the error's failure would create a loop.
  }
}

export default function useErrorTracker() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onError = (event) => {
      const errMsg = event?.message || event?.error?.message || 'Unknown';
      const errStack = event?.error?.stack || '';
      reportError(errMsg, errStack);
      // Do NOT return false — let other handlers run + let devtools show.
    };
    const onRejection = (event) => {
      const reason = event?.reason;
      const errMsg = reason?.message
        ? reason.message
        : (typeof reason === 'string' ? reason : 'Unhandled promise rejection');
      const errStack = reason?.stack || '';
      reportError(errMsg, errStack);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);
}
